import { PaymentMethodEnum } from 'common/enums/PaymentMethodEnum'
import { XenditFieldTypeEnum } from 'publisher/enums/XenditFieldTypeEnum'
import { usePayment } from 'publisher/store'
import {
  getPaymentMethods,
  getXenditPublicKey,
} from 'publisher/store/payment/paymentSelectors'
import loadScript from 'publisher/utils/loadScript'
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useOfferPricing } from 'src/publisher/hooks/use-offer-pricing'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'

export type Xendit = {
  setPublishableKey: (publicApiKey: string) => void
  card: {
    validateCardNumber: (cardNumber: string) => boolean
    validateExpiry: (month: string, year: string) => boolean
    validateCvn: (cvn: string) => boolean
    createToken: (value1: unknown, value2: unknown) => void
    createAuthentication: (value1: unknown, value2: unknown) => void
  }
}

interface XenditFieldsInterface {
  month: string
  year: string
  cardNumber: string
  cardHolderName: string
  cardHolderFirstName: string
  cardHolderLastName: string
  cvv: string
  amount?: number
}

interface XenditErrorInterface {
  cardNumber: string
  cardCvn: string
  expiryDate: string
  cardHolderName: string
}

export type XenditWrapper = {
  fields: XenditFieldsInterface
  changeFields: Record<string, Dispatch<SetStateAction<string>>>
  handleXenditVerification: (
    type: XenditFieldTypeEnum,
    value: string,
    value2?: string,
  ) => boolean
  errors: XenditErrorInterface
  resetError: (key: keyof XenditErrorInterface, value: string) => void
  isSecureModalVisible: boolean
  setIsSecureModalVisible: React.Dispatch<React.SetStateAction<boolean>>
  xenditOtpUrl: string
  setXenditOtpUrl: React.Dispatch<React.SetStateAction<string>>
}

declare global {
  interface Window {
    Xendit: Xendit
  }
}

const defaultErrors: XenditErrorInterface = {
  cardNumber: '',
  cardCvn: '',
  expiryDate: '',
  cardHolderName: '',
}

const XenditContext = createContext<XenditWrapper>({} as XenditWrapper)

export const useXendit = () => useContext(XenditContext)

export const XenditProvider = (
  props: React.PropsWithChildren<Record<string, never>>,
) => {
  const { t } = useTypedTranslation('publisher')
  const paymentMethods = usePayment(getPaymentMethods)
  const xenditPublicKey = usePayment(getXenditPublicKey)
  const [errors, setErrors] = useState(defaultErrors)
  const [isSecureModalVisible, setIsSecureModalVisible] = useState(false)
  const [xenditOtpUrl, setXenditOtpUrl] = useState<string>('')

  const { selectedOfferPricing } = useOfferPricing()
  const paymentAmount = selectedOfferPricing?.grossAmount ?? 0

  const [month, setMonth] = useState('')
  const [cardNumber, setCardNumber] = useState('')
  const [cardHolderName, setCardHolderName] = useState('')
  const [cardHolderFirstName, setCardHolderFirstName] = useState('')
  const [cardHolderLastName, setCardHolderLastName] = useState('')
  const [year, setYear] = useState('')
  const [cvv, setCvv] = useState('')

  useEffect(() => {
    if (
      [PaymentMethodEnum.XenditIdCard, PaymentMethodEnum.XenditPhCard].some(
        paymentMethod => paymentMethods.includes(paymentMethod),
      ) &&
      xenditPublicKey
    ) {
      loadScript(process.env.XENDIT_API_URL as string, 'xendit', async () => {
        window.Xendit.setPublishableKey(xenditPublicKey)
      })
    }
  }, [paymentMethods, xenditPublicKey])

  const addError = (key: keyof XenditErrorInterface, message: string) => {
    setErrors(errors => ({
      ...errors,
      [key]: message,
    }))
  }
  const resetError = (key: keyof XenditErrorInterface) => {
    setErrors(errors => ({
      ...errors,
      [key]: '',
    }))
  }

  const handleXenditVerification = (
    type: XenditFieldTypeEnum,
    value: string,
    value2 = '',
  ) => {
    if (type === XenditFieldTypeEnum.card_number) {
      if (!window.Xendit.card.validateCardNumber(value)) {
        addError(
          'cardNumber',
          t('components.payment_methods.xendit.invalid_card_number'),
        )
        return false
      }
    } else if (type === XenditFieldTypeEnum.expiry_date) {
      if (!window.Xendit.card.validateExpiry(value, value2)) {
        addError(
          'expiryDate',
          t('components.payment_methods.xendit.invalid_card_expiration_date'),
        )
        return false
      }
    } else if (type === XenditFieldTypeEnum.cvn_number) {
      if (!window.Xendit.card.validateCvn(value)) {
        addError(
          'cardCvn',
          t('components.payment_methods.xendit.invalid_cvn_number'),
        )
        return false
      }
    }

    if (
      type === XenditFieldTypeEnum.card_holder_first_name ||
      type === XenditFieldTypeEnum.card_holder_last_name
    ) {
      const trimmed = value.trim()
      if (!trimmed) {
        addError(
          'cardHolderName',
          t('components.payment_methods.xendit.invalid_card_holder_name'),
        )
        return false
      }
    }

    return true
  }

  const handleChangeHolderName = (value: SetStateAction<string>) => {
    setCardHolderName(value)

    const splitted = value
      .toString()
      .trim()
      .split(/\s+/)
      .filter(el => el)

    setCardHolderFirstName(splitted[0] ?? '')
    setCardHolderLastName(splitted.slice(1).join(' ') ?? '')

    if (splitted.length >= 2) {
      resetError('cardHolderName')
    }
  }

  return (
    <XenditContext.Provider
      value={{
        fields: {
          month: month,
          year: year,
          cardNumber: cardNumber,
          cardHolderName: cardHolderName,
          cardHolderFirstName: cardHolderFirstName,
          cardHolderLastName: cardHolderLastName,
          cvv: cvv,
          amount: paymentAmount,
        },
        changeFields: {
          onChangeMonth: setMonth,
          onChangeYear: setYear,
          onChangeCardNumber: setCardNumber,
          onChangeCardHolderName: handleChangeHolderName,
          onChangeCvv: setCvv,
        },
        handleXenditVerification,
        errors,
        resetError,
        isSecureModalVisible,
        setIsSecureModalVisible,
        xenditOtpUrl,
        setXenditOtpUrl,
      }}
    >
      {props.children}
    </XenditContext.Provider>
  )
}
